import React, { useState } from 'react';
import { Play, X, Zap, TrendingUp, Award, ExternalLink, CheckCircle2, Monitor, Apple } from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const Demo = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.3 });
  const [videoRef, videoVisible] = useScrollAnimation({ threshold: 0.2 });
  const [benchmarkRef, benchmarkVisible] = useScrollAnimation({ threshold: 0.2 });

  return (
    <section id="demo" className="py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center max-w-3xl mx-auto mb-12 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 mb-6">
            <Award className="w-4 h-4 text-green-400" />
            <span className="text-sm font-medium text-green-400">Verified December 2025</span>
          </div>

          <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-4 tracking-tight">
            See It. <span className="text-shimmer">Believe It.</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Watch the benchmark run live. See the numbers for yourself. No marketing fluff — just verified performance.
          </p>
        </div>

        {/* Product Video - PROMINENT */}
        <div
          ref={videoRef}
          className={`max-w-5xl mx-auto mb-16 transition-all duration-1000 ${
            videoVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'
          }`}
        >
          <div className="relative rounded-3xl overflow-hidden border-2 border-cyan-500/30 bg-gradient-to-br from-gray-900 via-gray-900 to-black shadow-2xl shadow-cyan-500/10">
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 via-transparent to-blue-500/5 pointer-events-none" />

            <div className="p-8 lg:p-10">
              <div className="grid lg:grid-cols-5 gap-8 items-center">
                {/* Video Player - Takes more space */}
                <div className="lg:col-span-3 relative group cursor-pointer" onClick={() => setIsVideoOpen(true)}>
                  <div className="absolute -inset-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative aspect-video rounded-xl bg-black border-2 border-cyan-500/30 overflow-hidden shadow-2xl">
                    {/* Video thumbnail/preview */}
                    <video
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                      poster="/benchmark-macos.png"
                    >
                      <source src="/demo-video.mp4" type="video/mp4" />
                      <source src="/demo-video.mov" type="video/quicktime" />
                    </video>
                    {/* Play overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/30 transition-colors">
                      <div className="w-24 h-24 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center group-hover:scale-110 transition-transform shadow-2xl shadow-cyan-500/50">
                        <Play className="w-10 h-10 text-white ml-1" />
                      </div>
                    </div>
                    {/* Badge */}
                    <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-green-500/90 text-white text-xs font-bold flex items-center gap-1">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                      </span>
                      LIVE BENCHMARK
                    </div>
                  </div>
                </div>

                {/* Video Info */}
                <div className="lg:col-span-2 space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-3">
                      Watch the Benchmark Run
                    </h3>
                    <p className="text-muted-foreground">
                      See Astra Express Lane achieve 954M decisions/sec throughput in real-time on a MacBook Pro M1.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                      <Zap className="w-5 h-5 text-cyan-400" />
                      <div>
                        <div className="text-white font-semibold">954M Decisions/sec</div>
                        <div className="text-xs text-muted-foreground">~1ns amortized per-decision</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-green-500/10 border border-green-500/20">
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                      <div>
                        <div className="text-white font-semibold">No FPGA Required</div>
                        <div className="text-xs text-muted-foreground">Pure software solution</div>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => setIsVideoOpen(true)}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold hover:opacity-90 transition-opacity shadow-lg shadow-cyan-500/25"
                  >
                    <Play className="w-5 h-5" />
                    Play Full Video
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Benchmark Screenshots - Side by Side */}
        <div
          ref={benchmarkRef}
          className={`max-w-6xl mx-auto transition-all duration-1000 delay-200 ${
            benchmarkVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-white mb-2">Cross-Platform Verification</h3>
            <p className="text-muted-foreground">Same engine, verified on both ARM and x86 architectures</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* macOS Benchmark */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/30 to-pink-500/30 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative p-6 rounded-2xl bg-gradient-to-br from-purple-500/10 via-gray-900 to-gray-900 border-2 border-purple-500/30">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <Apple className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <div className="text-white font-bold">macOS M1 Pro</div>
                      <div className="text-xs text-muted-foreground">ARM64 Architecture</div>
                    </div>
                  </div>
                  <span className="px-2 py-1 rounded-full bg-purple-500/20 text-purple-400 text-xs font-bold">FASTEST</span>
                </div>

                {/* Screenshot */}
                <div
                  className="relative rounded-xl overflow-hidden border border-purple-500/30 cursor-pointer hover:scale-[1.02] transition-transform"
                  onClick={() => setZoomedImage('macos')}
                >
                  <img
                    src="/benchmark-macos.png"
                    alt="ASTRA Benchmark on macOS M1 Pro - 1.11ns latency"
                    width={870}
                    height={487}
                    className="w-full"
                  />
                  <button className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-black/70 text-white text-xs hover:bg-black/90 transition-colors">
                    <ExternalLink className="w-3 h-3" />
                    Enlarge
                  </button>
                </div>

                {/* Stats */}
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-purple-500/10 text-center">
                    <div className="text-2xl font-bold text-purple-400">954M</div>
                    <div className="text-xs text-muted-foreground">Decisions/sec</div>
                  </div>
                  <div className="p-3 rounded-lg bg-purple-500/10 text-center">
                    <div className="text-2xl font-bold text-purple-400">~1ns*</div>
                    <div className="text-xs text-muted-foreground">Amortized</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Windows Benchmark */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/30 to-cyan-500/30 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative p-6 rounded-2xl bg-gradient-to-br from-blue-500/10 via-gray-900 to-gray-900 border-2 border-blue-500/30">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <Monitor className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <div className="text-white font-bold">Windows x86</div>
                      <div className="text-xs text-muted-foreground">AMD Ryzen 9 4900H (WSL2)</div>
                    </div>
                  </div>
                  <span className="px-2 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-bold">VERIFIED</span>
                </div>

                {/* Screenshot */}
                <div
                  className="relative rounded-xl overflow-hidden border border-blue-500/30 cursor-pointer hover:scale-[1.02] transition-transform"
                  onClick={() => setZoomedImage('windows')}
                >
                  <img
                    src="/benchmark-windows.png"
                    alt="ASTRA Benchmark on Windows - 1.93ns latency"
                    width={779}
                    height={901}
                    className="w-full"
                  />
                  <button className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-black/70 text-white text-xs hover:bg-black/90 transition-colors">
                    <ExternalLink className="w-3 h-3" />
                    Enlarge
                  </button>
                </div>

                {/* Stats */}
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-blue-500/10 text-center">
                    <div className="text-2xl font-bold text-blue-400">517M</div>
                    <div className="text-xs text-muted-foreground">Decisions/sec</div>
                  </div>
                  <div className="p-3 rounded-lg bg-blue-500/10 text-center">
                    <div className="text-2xl font-bold text-blue-400">~1.9ns*</div>
                    <div className="text-xs text-muted-foreground">Amortized</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Production Potential Section */}
          <div className="mt-12 p-8 rounded-2xl bg-gradient-to-r from-yellow-500/5 via-orange-500/5 to-red-500/5 border border-yellow-500/20">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-500/20 border border-yellow-500/30 mb-4">
                <Award className="w-5 h-5 text-yellow-400" />
                <span className="text-sm font-bold text-yellow-400">IMAGINE PRODUCTION</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                Consumer Laptops Today. <span className="text-gradient">Production Hardware Tomorrow.</span>
              </h3>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                These benchmarks were achieved on <span className="text-white font-medium">an Apple M1 Pro MacBook</span> and a <span className="text-white font-medium">5-year-old Windows laptop</span> —
                not million-dollar co-location servers. <span className="text-yellow-400 font-semibold">Production deployment targets AMD EPYC + BlueField-3 DPU architecture.</span>
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="p-5 rounded-xl bg-background/50 border border-border text-center">
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mx-auto mb-3">
                  <Apple className="w-6 h-6 text-purple-400" />
                </div>
                <div className="text-white font-bold mb-1">MacBook Pro M1</div>
                <div className="text-sm text-muted-foreground">954M decisions/sec throughput. ~1ns amortized. Consumer hardware.</div>
              </div>
              <div className="p-5 rounded-xl bg-background/50 border border-border text-center">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mx-auto mb-3">
                  <Monitor className="w-6 h-6 text-blue-400" />
                </div>
                <div className="text-white font-bold mb-1">5-Year-Old Laptop</div>
                <div className="text-sm text-muted-foreground">AMD Ryzen 9 4900H. 517M decisions/sec. Pipelined throughput.</div>
              </div>
              <div className="p-5 rounded-xl bg-background/50 border border-border text-center">
                <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center mx-auto mb-3">
                  <Zap className="w-6 h-6 text-yellow-400" />
                </div>
                <div className="text-white font-bold mb-1">Production Target</div>
                <div className="text-sm text-muted-foreground">AMD EPYC + BlueField-3 DPU. Target: 150-250ns tick-to-trade with FPGA.</div>
              </div>
            </div>

            <div className="text-center p-4 rounded-xl bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20">
              <p className="text-yellow-400 font-medium text-sm">
                <CheckCircle2 className="w-4 h-4 inline mr-2" />
                First known pure-software engine achieving 950M+ decisions/sec. Pipelined throughput benchmark.
              </p>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="mt-10 text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-4 p-6 rounded-2xl bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-purple-500/10 border border-cyan-500/20">
              <div className="text-center sm:text-left">
                <div className="text-white font-bold text-lg">Interested in learning more?</div>
                <div className="text-sm text-muted-foreground">Connect with our team for investor inquiries and technical briefings</div>
              </div>
              <a
                href="#contact"
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4" />
                Get In Touch
              </a>
            </div>
          </div>
        </div>

        {/* Video Modal */}
        {isVideoOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm"
            onClick={() => setIsVideoOpen(false)}
          >
            <div
              className="relative w-full max-w-6xl mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsVideoOpen(false)}
                className="absolute -top-12 right-0 p-2 text-white hover:text-cyan-400 transition-colors"
              >
                <X className="w-8 h-8" />
              </button>
              <video
                className="w-full rounded-xl shadow-2xl"
                controls
                autoPlay
              >
                <source src="/demo-video.mp4" type="video/mp4" />
                <source src="/demo-video.mov" type="video/quicktime" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        )}

        {/* Image Zoom Modal */}
        {zoomedImage && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm cursor-zoom-out"
            onClick={() => setZoomedImage(null)}
          >
            <div className="relative max-w-[95vw] max-h-[95vh]">
              <button
                onClick={() => setZoomedImage(null)}
                className="absolute -top-12 right-0 p-2 text-white hover:text-cyan-400 transition-colors"
              >
                <X className="w-8 h-8" />
              </button>
              <img
                src={zoomedImage === 'macos' ? '/benchmark-macos.png' : '/benchmark-windows.png'}
                alt={`ASTRA Benchmark Results - ${zoomedImage === 'macos' ? 'macOS M1 Pro' : 'Windows x86'}`}
                className="max-w-full max-h-[90vh] object-contain rounded-xl border-2 border-cyan-500/50 shadow-2xl"
              />
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/70 text-white text-sm">
                Click anywhere to close
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Demo;
